<template>
  <div>
    <HelpTip v-bind="labelInfo"></HelpTip>
    <van-field
      class="select-temp work-temp-form-item"
      :class="{ 'select-temp--picker': selfServiceSelect }"
      :name="id"
      :rules="[
        {
          required: required,
          message: languages.smartOrderPleaseChoose + label
        }
      ]"
      :value="curValue"
      :readonly="selfServiceSelect"
      :clickable="selfServiceSelect"
      @click="onFieldClick"
    >
      <template v-if="selfServiceSelect" #input>
        <div
          class="select select--picker-trigger"
          :class="{ 'placeholder-style': isPlaceholder }"
        >
          <span class="select--picker-text van-ellipsis">{{ displayLabel }}</span>
          <van-icon
            class="select-arrow"
            :class="{ 'placeholder-style': disabled }"
            name="arrow-down"
            size="24"
          />
        </div>
      </template>
      <template v-else #input>
        <div class="select">
          <select
            ref="selectEle"
            style="
              float: left;
              z-index: 0;
              position: relative;
              width: 100%;
              height: 100%;
              background-color: transparent;
              color: inherit;
              outline: inherit;
              border: 0;
              font-size: inherit;
              cursor: pointer;
            "
            v-model="curValue"
            :disabled="disabled"
            :class="[
              {
                'placeholder-style':
                  customKey.defaultValue == undefined || disabled
              },
              pointClass
            ]"
            @change="onSelect($event.target.value)"
          >
            <option v-if="placeholder" disabled selected :value="undefined">
              {{ placeholder }}
            </option>
            <option
              v-for="item in customKey.options"
              :value="item.value"
              :key="item.value"
            >
              {{ item.label }}
            </option>
          </select>
          <van-icon
            class="select-arrow"
            :class="{
              'placeholder-style': disabled
            }"
            name="arrow-down"
            size="24"
          />
        </div>
      </template>
    </van-field>
    <van-popup
      v-if="selfServiceSelect"
      v-model="showPicker"
      position="bottom"
      class="temp-select-popup"
      get-container="body"
      :safe-area-inset-bottom="true"
    >
      <van-picker
        show-toolbar
        :title="label"
        :columns="pickerColumns"
        :default-index="pickerDefaultIndex"
        @confirm="onPickerConfirm"
        @cancel="showPicker = false"
      />
    </van-popup>
  </div>
</template>

<script>
import workTemp from '@mixins/officialSupport/workTemp';
export default {
  name: 'TempSelect',
  mixins: [workTemp],
  model: {
    prop: 'value',
    event: 'change'
  },
  props: {
    disabled: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: ''
    },
    pointClass: {
      type: String,
      default: ''
    },
    /** 自助表单：用 van-picker 替代原生 select，避免 Android WebView 底部留白 */
    selfServiceSelect: {
      type: Boolean,
      default: false
    },
    customKey: {
      type: Object,
      default: () => ({
        options: {
          type: Array,
          default: () => []
        },
        defaultValue: {
          default: undefined
        }
      })
    }
  },
  data() {
    return {
      curValue: null,
      showPicker: false
    };
  },
  computed: {
    optionList() {
      const raw = this.customKey?.options;
      return Array.isArray(raw) ? raw : [];
    },
    pickerColumns() {
      return this.optionList.map(item => item.label);
    },
    pickerDefaultIndex() {
      const idx = this.optionList.findIndex(
        item => item.value === this.curValue
      );
      return idx >= 0 ? idx : 0;
    },
    displayLabel() {
      if (this.isPlaceholder) {
        return this.placeholder || '';
      }
      const hit = this.optionList.find(item => item.value === this.curValue);
      return hit ? hit.label : String(this.curValue ?? '');
    },
    isPlaceholder() {
      return (
        this.curValue === null ||
        this.curValue === undefined ||
        this.curValue === ''
      );
    }
  },
  watch: {
    value(val) {
      if (val == this.curValue) return;
      this.curValue = val;
    }
  },
  created() {
    this.curValue = this.customKey.defaultValue;
    this.formData[this.id] = this.customKey.defaultValue;
  },
  methods: {
    onFieldClick() {
      if (!this.selfServiceSelect || this.disabled) {
        return;
      }
      this.showPicker = true;
    },
    onPickerConfirm(_value, index) {
      const option = this.optionList[index];
      if (option) {
        this.onSelect(option.value);
      }
      this.showPicker = false;
    },
    onSelect(value) {
      this.curValue = value;
      this.$emit('change', value);
      this.formData[this.id] = value;
      const selectEle = this.$refs.selectEle;
      selectEle && typeof selectEle.blur === 'function' && selectEle.blur();
    },
    selectAutoBlur() {
      if (document?.visibilityState == 'hidden') {
        try {
          const selectElem = this.$refs.selectEle;
          selectElem &&
            typeof selectElem.blur === 'function' &&
            selectElem.blur();
        } catch (error) {
          console.error(error);
        }
      }
    }
  },
  mounted() {
    if (!this.selfServiceSelect) {
      document.addEventListener('visibilitychange', this.selectAutoBlur);
    }
  },
  beforeDestroy() {
    if (!this.selfServiceSelect) {
      document.removeEventListener('visibilitychange', this.selectAutoBlur);
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@assets/scss/officialSupport/workTempForm.scss';
.select {
  display: flex;
  position: relative;
  width: 100%;
  height: 64px;
  padding: 0 17px 0 20px;
  background-color: var(--block-bg-light-color);
  border-radius: var(--border-radius);
  font-size: inherit;
  color: var(--text-color);
  box-sizing: border-box;
  border: 1px solid var(--border-color);

  &--picker-trigger {
    align-items: center;
    cursor: pointer;
  }

  &--picker-text {
    flex: 1;
    min-width: 0;
    line-height: 1.2;
  }

  > select {
    // iOS原生select自带padding
    padding: 0;
    // 去除原生下拉箭头
    -webkit-appearance: none; /* Safari 和 Chrome */
    -moz-appearance: none; /* Firefox */
    appearance: none; /* 其他浏览器 */
    &:disabled {
      color: var(--placeholder-color) !important;
      -webkit-text-fill-color: var(--placeholder-color) !important;
    }
  }

  .placeholder-style {
    color: var(--placeholder-color) !important;
  }

  &-arrow {
    z-index: 0;
    position: relative;
    top: 0;
    right: 0;
    bottom: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    margin: auto;
    font-weight: bold;
    color: #9e9e9e !important;
  }
}
</style>
